import Request from 'src/utils/request';
import { registerAppError, LoadDataError } from 'src/utils/register-app-error';

const R = new Request();

type StatusCode = 'OK' | 'ERROR';

interface DecryptPayloadResponse {
    status: StatusCode;
    data: DecryptedCallInfo;
}

export interface DecryptedCallInfo {
    phone?: string;
    reason?: number;
    iin?: string;
    lang?: string;
    name?: string;
    surname?: string;
    operator?: string;
    bin?: string;
    company?: string;
    additionalInfo?: string;
    sourceUrl?: string;
    [key: string]: string | number | undefined;
}

export async function decryptPayload(payload: string): Promise<DecryptPayloadResponse> {
    try {
        const response = await R.callBrokerApi({
            controller: '/crypto/decrypt',
            data: {
                payload,
            },
        });

        response.data = JSON.parse(response.data);
        return response as DecryptPayloadResponse;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToDecryptPayload, e);
        return null;
    }
}

export interface RegisterParticipantRequest {
    phone?: string;
    lang?: string;
    iin?: string;
    bin?: string;
    company?: string;
    additionalInfo?: string;
    name?: string;
    surname?: string;
    callHash?: string;
    [key: string]: string | number | undefined;
}

export interface RegisterParticipantResponse {
    status: StatusCode;
    id: string;
    url: string;
    callUrl: string;
    conferenceUrl: string;
    sipUrl?: string;
}

export async function registerParticipant(data: RegisterParticipantRequest): Promise<RegisterParticipantResponse> {
    try {
        const isLegalEntity = window.VBwidgetConfig.interfaceType === 'legal-entity';
        const response = await R.callExternalApi({ 
            controller: '/participant/create',
            data: {
                IN_PAYLOAD: JSON.stringify({
                    isLegalEntity: isLegalEntity ? isLegalEntity : undefined,
                    ...data
                }),
            },
        });

        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        const formattedResponse: RegisterParticipantResponse = {
            status: response.status,
            id: response.data[0].id,
            url: response.data[0].url,
            callUrl: response.data[0].callUrl,
            conferenceUrl: response.data[0].conferenceUrl,
            sipUrl: response.data[0].sipUrl,
        };

        return formattedResponse as RegisterParticipantResponse;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToCreateParticipant, e);
        throw e;
    }
}

export interface CallInfo {
    CAL_REQUEST_BODY: string;
    REASON_KZ: string;
    REASON_RU: string;
    OPERATOR_NAME: string;
    status: string;
}

export async function getCallnfo(callHash: string): Promise<CallInfo | null> {
    try {
        const response = await R.callExternalApi({ 
            controller: '/call/info',
            data: {
                callHash,
            },
        });

        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        const formattedResponse: CallInfo = {
            status: response.status,
            CAL_REQUEST_BODY: response.data[0].CAL_REQUEST_BODY,
            REASON_KZ: response.data[0].REASON_KZ,
            REASON_RU: response.data[0].REASON_RU,
            OPERATOR_NAME: response.data[0].OPERATOR_NAME,
        };

        return formattedResponse as CallInfo;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToLoadCallInfo, e);
        return null;
    }
}

export interface RequestCallResponse {
    callHash: string;
    status: StatusCode;
}

export async function requestCall(data: {
    reason: number;
    operator?: string;
    iin?: string;
    phone?: string;
    bin?: string;
    name?: string;
    surname?: string;
    sourceUrl?: string;
    sourceTag?: string;
    sourceHash?: string;
    lang?: string;
}): Promise<RequestCallResponse | null> {
    const { operator } = data;
    const isLegalEntity = window.VBwidgetConfig.interfaceType === 'legal-entity';
    const operatorId = (!operator || operator === '') ? undefined : operator;
    try {
        const response = await  R.callExternalApi({ 
            controller: '/call/request',
            data: {
                IN_PAYLOAD: JSON.stringify({
                    ...data,
                    operator: operatorId,
                    isLegalEntity: isLegalEntity ? isLegalEntity : undefined,
                }),
            },
        });

        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        const formattedResponse: RequestCallResponse = {
            status: response.status,
            callHash: response.data[0].callHash,
        };

        return formattedResponse as RequestCallResponse;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.CallRequestFailed, e);
        return null;
    }
}

export interface CallReason {
    code: number,
    nameRu: string;
    nameKz: string;
    nameEn?: string;
}

interface GetCallReasonsResponse {
    list: CallReason[];
    status: StatusCode
}

export type SourceType = 'widget-cc' | 'widget-market' | 'source-registrator';

export async function getReasons(
    forLegalEntities = false,
    source: SourceType = 'widget-cc'
): Promise<GetCallReasonsResponse | null> {
    try {
        const response = await R.callExternalApi({ 
            controller: '/call/getReasons',
            data: {
                forLegalEntities: forLegalEntities ? 1 : 0,
                source,
            },
        });

        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        // меняем формат под старый    
        const formattedResponse: GetCallReasonsResponse = {
            status: response.status,
            list: Array.isArray(response.data) ? response.data : [],
        };

        return formattedResponse as GetCallReasonsResponse;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToGetCallReasons, e);
        return null;
    }
}

interface RegisterAppointmentResponse {
    status: StatusCode;
    message?: string;
}

/**
 * @deprecated Похоже данная функция не вызыватеся в интерфейсе..
 */
export async function registerAppointment(data: { phone: string, iin: string, reason: number, name: string, surname: string, operator?: string }): Promise<RegisterAppointmentResponse> {
    try {
        const response = await R.callBrokerApi({
            controller: '/call/appointment',
            data,
        });

        return response as RegisterAppointmentResponse;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToSetAppointment, e);
        return null;
    }
}

export interface WorkTimeSettings {
    status: StatusCode,
    isCallPossible: boolean,
    messageRu: string;
    messageKz: string;
    message?: string;
}

export async function getWorkTimeSettings(companyCode?: number): Promise<WorkTimeSettings> {
    try {
        const response = await R.callExternalApi({ 
            controller: '/settings/worktime',
            data: companyCode ? { companyCode } : null,
        });

        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        const formattedResponse: WorkTimeSettings = {
            status: response.status,
            isCallPossible: true, // response.data[0].isCallPossible === 'true', // debug
            messageRu: response.data[0].messageRu,
            messageKz: response.data[0].messageKz,
            message: response.message,
        };

        return formattedResponse as WorkTimeSettings;
    } catch (e) {
        console.error(e);
        return null;
    }
}

export interface FieldSelectListInterface {
    code: number;
    isActive: boolean;
    selected: boolean;
}

export interface FieldSettingsInterface {
    list?: FieldSelectListInterface[];
    link?: string;
    linkKz?: string;
    linkEn?: string;
    agreementChecked?: boolean;
    value?: string;
    paramName?: string;
}

export interface FormFieldInterface extends FieldInterface {
    mask: string;
    pattern: string;
}

export interface FieldInterface {
    key: string;
    type: string;
    isRequired: boolean;
    labelKz?: string;
    labelRu?: string;
    labelEn?: string;
    settings?: FieldSettingsInterface;
    order?: number;
    multipleValues?: boolean;
    shouldExist?: boolean;
    isVisible?: boolean;
}

export type AnalyticsScriptPlacement = 'head_start' | 'head_end' | 'body_start' | 'body_end';

export interface SourceInterface {
    ignoreWorkTime: boolean;
    callType: 'video' | 'audio' | 'sip';
    showFirstPage: boolean;
    fieldList: FieldInterface[];
    companyCode?: number;
    analyticsScriptCode?: string | null;
    analyticsScriptContent?: string;
    analyticsScriptPlacement?: AnalyticsScriptPlacement;
}

export interface GetSourceResponseInterface {
    status: StatusCode;
    source: SourceInterface;
    message?: string;
}

export async function getSourceByHash(
    sourceHash: string
): Promise<GetSourceResponseInterface | null> {
    try {
        const response = await R.callExternalApi({ 
            controller: '/admin/sources/getSourceByHash',
            data: { hash: sourceHash },
        });
        
        if (!response.data?.length) {
            throw new Error('response.data is empty');
        }

        const formattedResponse: GetSourceResponseInterface = {
            status: response.status,
            //source: response.data[0],
            source: {
                ...response.data[0],
                fieldList: JSON.parse(response.data[0].fieldList),
            },
            message: response.message
        };

console.error(formattedResponse);

        return formattedResponse as GetSourceResponseInterface;
    } catch (e) {
        console.error(e);
        registerAppError(LoadDataError.FailedToGetSource, e);
        return null;
    }
}
